import { forwardRef, useLayoutEffect, useRef } from 'react';
import { cn } from './uiTheme';

export function resizeTextarea(el: HTMLTextAreaElement | null, maxHeight?: number) {
  if (!el) return;
  el.style.height = 'auto';
  const styles = getComputedStyle(el);
  const minH = Number.parseFloat(styles.minHeight) || 0;
  let next = el.scrollHeight;
  if (minH) next = Math.max(next, minH);
  if (maxHeight != null) {
    next = Math.min(next, maxHeight);
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  } else {
    el.style.overflowY = 'hidden';
  }
  el.style.height = `${next}px`;
}

/**
 * Textarea that grows vertically with content (no manual resize handle).
 */
interface AutoGrowTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string;
  maxHeight?: number;
}

const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(function AutoGrowTextarea(
  { className = '', maxHeight, onInput, value, ...props },
  ref
) {
  const innerRef = useRef<any>(null);

  useLayoutEffect(() => {
    resizeTextarea(innerRef.current, maxHeight);
  });

  return (
    <textarea
      ref={(node) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      value={value}
      rows={1}
      className={cn('input-mdl resize-none overflow-hidden', className)}
      onInput={(e) => {
        resizeTextarea(e.currentTarget, maxHeight);
        onInput?.(e);
      }}
      {...props}
    />
  );
});

AutoGrowTextarea.displayName = 'AutoGrowTextarea';

export default AutoGrowTextarea;
